import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { ConcentricRingLogo } from "@v2/components/ConcentricRingLogo";
import { ConnectShopifyIntegrationCta } from "@v2/components/ConnectShopifyCallout";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useShopifyProductEligibility } from "@v2/features/products/hooks/useShopifyProductEligibility";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { X } from "lucide-react";
import { z } from "zod";

const ShopifyReploIllustration: React.FC = () => {
  return (
    <div className="hidden md:flex items-center justify-center -mx-18 -mb-20 -mt-2 pointer-events-none">
      <ConcentricRingLogo
        variant="shopify"
        isSelected={false}
        hoverDirection="left"
        className="w-64 h-64 scale-125"
      />
      <ConcentricRingLogo
        variant="replo"
        isSelected={false}
        hoverDirection="right"
        className="w-64 h-64 -ml-18 scale-125 hidden md:block"
      />
    </div>
  );
};

function useShopifyProductImportPromptVisibility() {
  const { project } = useCurrentProjectContext();
  const projectId = project?.id;

  const { isEligibleForShopifyProducts, isLoading } =
    useShopifyProductEligibility({
      projectId,
      project,
    });

  const [isDismissed, setIsDismissed] = useLocalStorageState(
    projectId
      ? `replo.project.${projectId}.dismissedShopifyImportPrompt`
      : null,
    false,
    { schema: z.boolean() },
  );

  const shouldShow = Boolean(
    isEligibleForShopifyProducts &&
      !project?.integrations?.shopify &&
      !isDismissed &&
      !isLoading,
  );

  const handleDismiss = React.useCallback(() => {
    setIsDismissed(true);
  }, [setIsDismissed]);

  return { shouldShow, handleDismiss, projectId };
}

export const ShopifyProductImportPrompt: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const { shouldShow, handleDismiss, projectId } =
    useShopifyProductImportPromptVisibility();

  if (!shouldShow || !projectId) {
    return null;
  }

  return (
    <div className="border-0.5 border-border flex flex-col gap-6 rounded-xl p-6 shadow-lg fixed bottom-9 left-4 right-4 md:left-auto md:right-9 w-[calc(100vw-2rem)] md:w-90 bg-white overflow-hidden z-50 group">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between">
          <h3 className="text-base font-semibold text-default">
            Sync your Shopify store
          </h3>
          <Button
            variant="secondary"
            size="sm"
            icon={true}
            onClick={handleDismiss}
            aria-label="Close"
          >
            <X />
          </Button>
        </div>
        <p className="text-xs text-muted">
          We'll automatically sync products from your Shopify store and replace
          any template products.
        </p>
      </div>

      {workspaceId && (
        <ConnectShopifyIntegrationCta
          cookieValue={{
            type: "productPicker",
            projectId,
            workspaceId,
          }}
          ctaText="Add Integration"
          size="base"
          showEndIcon
          className="w-fit"
        />
      )}

      <ShopifyReploIllustration />
    </div>
  );
};
